import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Chip,
    Button,
    Skeleton,
    Fade,
    IconButton,
} from '@mui/material';
import {
    Search as SearchIcon,
    Description,
    ShoppingCart,
    Business,
    AttachFile,
    Comment,
    Close
} from '@mui/icons-material';
import apiService from '../../services/api.service';

interface SearchResult {
    type: string;
    id: string;
    score: number;
    highlight?: any;
    source: any;
}

interface SearchResultsProps {
    open: boolean;
    query: string;
    onClose: () => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({ open, query, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<{
        total: number;
        hits: SearchResult[];
        took: number;
    }>({ total: 0, hits: [], took: 0 });
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open || query.length < 2) {
            console.log('[SearchResults] Not fetching - open:', open, 'query length:', query.length);
            setResults({ total: 0, hits: [], took: 0 });
            setSuggestions([]);
            return;
        }

        console.log('[SearchResults] Starting search for:', query);

        let debounceTimer: NodeJS.Timeout;
        const fetchResults = async () => {
            console.log('[SearchResults] Executing search fetch...');
            setLoading(true);
            try {
                const data = await apiService.globalSearch(query);
                console.log('[SearchResults] Search API response:', data);
                console.log('[SearchResults] Number of hits:', data.data?.hits?.length || 0);
                setResults(data);
            } catch (error) {
                console.error('[SearchResults] Search API error:', error);
            } finally {
                setLoading(false);
            }
        };

        const fetchSuggestions = async () => {
            console.log('[SearchResults] Executing autocomplete fetch...');
            try {
                const data = await apiService.getSearchAutocomplete(query);
                console.log('[SearchResults] Autocomplete API response:', data);
                console.log('[SearchResults] Number of suggestions:', data?.length || 0);
                setSuggestions(data);
            } catch (error) {
                console.error('[SearchResults] Autocomplete error:', error);
            }
        };

        debounceTimer = setTimeout(() => {
            fetchResults();
            fetchSuggestions();
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [query, open]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, onClose]);

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'prs':
                return <ShoppingCart sx={{ color: '#0a6ed1', fontSize: 20 }} />;
            case 'pos':
                return <Description sx={{ color: '#0a6ed1', fontSize: 20 }} />;
            case 'vendors':
                return <Business sx={{ color: '#F0AB00', fontSize: 20 }} />;
            case 'documents':
                return <AttachFile sx={{ color: '#0a6ed1', fontSize: 20 }} />;
            case 'comments':
                return <Comment sx={{ color: '#0a6ed1', fontSize: 20 }} />;
            default:
                return <SearchIcon sx={{ color: '#757575', fontSize: 20 }} />;
        }
    };

    const getTypeLabel = (type: string) => {
        switch (type) {
            case 'prs':
                return 'Purchase Requisition';
            case 'pos':
                return 'Purchase Order';
            case 'vendors':
                return 'Vendor';
            case 'documents':
                return 'Document';
            case 'comments':
                return 'Comment';
            default:
                return type;
        }
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'prs':
                return '#0a6ed1';
            case 'pos':
                return '#0a6ed1';
            case 'vendors':
                return '#F0AB00';
            case 'documents':
                return '#0a6ed1';
            case 'comments':
                return '#0a6ed1';
            default:
                return '#757575';
        }
    };

    const highlightText = (text: string, highlights?: any) => {
        if (!highlights || !text) return text;

        const keys = Object.keys(highlights);
        let highlightedText = text;

        keys.forEach(key => {
            const fragments = highlights[key];
            if (fragments && Array.isArray(fragments)) {
                fragments.forEach((fragment: string) => {
                    const cleanFragment = fragment.replace(/<em>/g, '').replace(/<\/em>/g, '');
                    highlightedText = highlightedText.replace(
                        new RegExp(cleanFragment, 'gi'),
                        () => fragment
                    );
                });
            }
        });

        return highlightedText;
    };

    const getResultTitle = (hit: SearchResult) => {
        const source = hit.source;
        switch (hit.type) {
            case 'prs':
                return source.prNumber || `PR #${source.prId}`;
            case 'pos':
                return source.poNumber || `PO #${source.poId}`;
            case 'vendors':
                return source.vendorName || source.vendorCode;
            case 'documents':
                return source.fileName;
            case 'comments':
                return `Comment #${source.commentId}`;
            default:
                return 'Unknown';
        }
    };

    const getResultDescription = (hit: SearchResult) => {
        const source = hit.source;
        switch (hit.type) {
            case 'prs':
                return highlightText(source.description || source.comments || 'No description', hit.highlight);
            case 'pos':
                return highlightText(source.description || source.comments || 'No description', hit.highlight);
            case 'vendors':
                return `${source.vendorName} - ${source.contactPerson || 'No contact'}`;
            case 'documents':
                return highlightText(source.fileName || 'Unnamed document', hit.highlight);
            case 'comments':
                return highlightText(source.content || '', hit.highlight);
            default:
                return '';
        }
    };

    const handleResultClick = (hit: SearchResult) => {
        let path = '';
        switch (hit.type) {
            case 'prs':
                path = `/pr/${hit.source.prId}`;
                break;
            case 'pos':
                path = `/po/${hit.source.poId}`;
                break;
            case 'vendors':
                path = '/companies';
                break;
            case 'documents':
                path = '/documents';
                break;
            default:
                return;
        }

        window.location.href = path;
    };

    const suggestionsByType = suggestions.reduce((acc, sugg) => {
        if (!acc[sugg.type]) acc[sugg.type] = [];
        acc[sugg.type].push(sugg);
        return acc;
    }, {} as Record<string, any[]>);

    return (
        <Fade in={open} timeout={200}>
            <Box
                ref={containerRef}
                sx={{
                    position: 'fixed',
                    top: 56,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100%',
                    maxWidth: 900,
                    zIndex: 9999,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                }}
            >
                {suggestions.length > 0 && showSuggestions && (
                    <Paper
                        sx={{
                            p: 1.5,
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(247, 247, 247, 0.99) 100%)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: 1.5,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.08)',
                            border: '1px solid rgba(0, 0, 0, 0.08)'
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 600, color: '#2c3e50' }}>
                                Suggestions
                            </Typography>
                            <IconButton size="small" onClick={() => setShowSuggestions(false)} sx={{ color: '#757575' }}>
                                <Close sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {Object.keys(suggestionsByType).map(type => (
                                <Box key={type}>
                                    <Typography variant="subtitle2" sx={{ fontSize: 11, fontWeight: 600, color: getTypeColor(type), mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        {getTypeLabel(type)}
                                    </Typography>
                                    <List dense sx={{ py: 0.5 }}>
                                        {suggestionsByType[type].slice(0, 3).map((sugg: any, idx: number) => (
                                            <ListItem
                                                key={idx}
                                                button
                                                onClick={() => {
                                                    setGlobalSearchQuery(sugg.text);
                                                    setShowSuggestions(false);
                                                }}
                                                sx={{
                                                    borderRadius: 1,
                                                    py: 0.75,
                                                    px: 1,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(10, 110, 209, 0.08)',
                                                    }
                                                }}
                                            >
                                                <ListItemText
                                                    primary={sugg.text}
                                                    primaryTypographyProps={{
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                        color: '#2c3e50'
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            ))}
                        </Box>

                        {!showSuggestions && (
                            <Button
                                size="small"
                                onClick={() => setShowSuggestions(true)}
                                sx={{ textTransform: 'none', fontSize: 12 }}
                            >
                                Show suggestions
                            </Button>
                        )}
                    </Paper>
                )}

                {loading ? (
                    <Paper
                        sx={{
                            p: 2,
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(247, 247, 247, 0.99) 100%)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: 1.5,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.08)'
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Skeleton variant="text" width="60%" height={32} />
                            <Skeleton variant="text" width="100%" height={24} />
                            <Skeleton variant="text" width="80%" height={24} />
                            <Skeleton variant="text" width="90%" height={24} />
                            <Skeleton variant="text" width="70%" height={24} />
                        </Box>
                    </Paper>
                ) : results.hits.length > 0 ? (
                    <Paper
                        sx={{
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(247, 247, 247, 0.99) 100%)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: 1.5,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.08)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            maxHeight: 'calc(100vh - 200px)',
                            overflow: 'auto'
                        }}
                    >
                        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                            <Typography variant="body2" sx={{ fontSize: 12, color: '#757575' }}>
                                Found {results.total} results in {results.took}ms
                            </Typography>
                        </Box>

                        <List disablePadding>
                            {results.hits.map((hit, index: number) => (
                                <React.Fragment key={`${hit.type}-${hit.id}`}>
                                    <ListItem
                                        button
                                        onClick={() => handleResultClick(hit)}
                                        sx={{
                                            py: 1.5,
                                            px: 2,
                                            borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: 'rgba(10, 110, 209, 0.04)',
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 48 }}>
                                            {getTypeIcon(hit.type)}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontSize: 13,
                                                            fontWeight: 600,
                                                            color: '#2c3e50',
                                                            lineHeight: 1.4
                                                        }}
                                                    >
                                                        {getResultTitle(hit)}
                                                    </Typography>
                                                    <Chip
                                                        label={getTypeLabel(hit.type)}
                                                        size="small"
                                                        sx={{
                                                            background: getTypeColor(hit.type) + '20',
                                                            color: getTypeColor(hit.type),
                                                            fontSize: 10,
                                                            fontWeight: 600,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: 0.5,
                                                            borderRadius: 1
                                                        }}
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: 12,
                                                        color: '#6a6d70',
                                                        lineHeight: 1.4,
                                                        '& em': {
                                                            color: '#0a6ed1',
                                                            fontWeight: 600,
                                                            backgroundColor: 'rgba(10, 110, 209, 0.1)',
                                                            padding: '0 2px',
                                                            borderRadius: 0.5
                                                        }
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: getResultDescription(hit)
                                                    }}
                                                />
                                            }
                                        />
                                    </ListItem>
                                    {index < results.hits.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    </Paper>
                ) : !loading && query.length >= 2 && results.total === 0 ? (
                    <Paper
                        sx={{
                            p: 4,
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(247, 247, 247, 0.99) 100%)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: 1.5,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.08)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            textAlign: 'center'
                        }}
                    >
                        <SearchIcon sx={{ fontSize: 48, color: '#cfd8dc', mb: 2 }} />
                        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600, color: '#2c3e50', mb: 1 }}>
                            No results found
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 13, color: '#757575', mb: 2 }}>
                            Try different keywords or check for typos
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 12, color: '#9e9e9e' }}>
                            Debug: {results.total} total, {results.hits.length} hits returned
                        </Typography>
                    </Paper>
                ) : null}
            </Box>
        </Fade>
    );
};

const setGlobalSearchQuery = (query: string) => {
    const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
    if (searchInput) {
        searchInput.value = query;
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
};

export default SearchResults;
